import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ScheduleTabs, { useTabParam } from "@/components/ScheduleTabs";
import ArchiveTab from "@/components/shownotes/ArchiveTab";
import WorkTab from "@/components/shownotes/WorkTab";
import RunTimesTab from "@/components/shownotes/RunTimesTab";
import SceneTrendsTab from "@/components/shownotes/SceneTrendsTab";
import NoteForm from "@/components/shownotes/NoteForm";
import NoteView from "@/components/shownotes/NoteView";
import NewNotesButton from "@/components/shownotes/NewNotesButton";
import ConfirmDialog from "@/components/ConfirmDialog";
import { noteTitle } from "@/components/shownotes/meta";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

// Show Notes: directors review/publish; stage managers submit; cast & crew browse the archive.
export default function ShowNotesPage() {
  const { user, viewOnly } = useAuth();
  const director = user.role === "coach";
  const [setup, setSetup] = useState(null);
  const [form, setForm] = useState(null);
  const [view, setView] = useState(null);
  const [reload, setReload] = useState(0);
  const [pending, setPending] = useState(0);
  const [params, setParams] = useSearchParams();
  const keys = director ? ["review", "archive", "runtimes", "scenes"]
    : ["archive", "runtimes", ...(setup?.is_stage_manager ? ["scenes"] : []), ...(setup?.can_submit ? ["mine"] : [])];
  const [tab, setTab] = useTabParam(keys);
  const noteId = params.get("note");

  useEffect(() => { api.get("/show-notes/setup").then(({ data }) => setSetup(data)).catch(() => setSetup({ productions: [] })); }, []);
  useEffect(() => {
    if (!noteId) return;
    api.get(`/show-notes/${noteId}`).then(({ data }) => open(data)).catch(() => {});
    setParams((p) => { p.delete("note"); return p; }, { replace: true });
  }, [noteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const editable = (n) => n.status !== "published" && (director || n.status !== "draft");
  const open = (n) => (editable(n) ? setForm({ note: n }) : setView(n));
  const changed = () => { setForm(null); setReload((r) => r + 1); window.dispatchEvent(new Event("rpg:badges-refresh")); };
  const manage = director && !viewOnly;
  const [del, setDel] = useState(null);
  const remove = async () => {
    try { await api.delete(`/show-notes/${del.id}`); toast.success("Notes deleted"); setView(null); changed(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDel(null); }
  };

  if (!setup) return <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const tabs = keys.map((k) => ({ key: k, label: { review: "Review", archive: "Archive", runtimes: "Run Times", scenes: "Scene Trends", mine: "My reports" }[k], badge: k === "review" ? pending : 0 }));
  const productions = director ? setup.productions : setup.productions.filter((p) => (user.subgroup_ids || []).includes(p.id));
  return (
    <div>
      <PageHeader title="Show Notes" subtitle={director ? "Review, approve and publish Rehearsal and Show Notes to each production" : "Rehearsal and Show Notes for your productions"}
        actions={setup.can_submit && !viewOnly && <NewNotesButton onPick={(kind) => setForm({ kind })} />} />
      <ScheduleTabs tabs={tabs} value={tab} onChange={setTab} testId="notes" />
      {tab === "archive" ? (
        <ArchiveTab productions={productions} onOpen={setView} reload={reload} canDownload={director} onDelete={manage ? setDel : undefined} />
      ) : tab === "runtimes" ? (
        <RunTimesTab productions={productions} />
      ) : tab === "scenes" ? (
        <SceneTrendsTab productions={setup.productions} director={director && !viewOnly} />
      ) : (
        <WorkTab mode={tab} userId={user.id} onOpen={open} reload={reload} onChanged={changed} onCount={setPending} viewOnly={viewOnly} />
      )}
      <NoteForm open={!!form} onOpenChange={(o) => !o && setForm(null)} kind={form?.kind} note={form?.note} setup={setup} viewOnly={viewOnly} onSaved={changed} />
      <NoteView note={view} onClose={() => setView(null)} canDownload={director}
        onEdit={manage ? (n) => { setView(null); setForm({ note: n }); } : undefined} onDelete={manage ? setDel : undefined} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} destructive onConfirm={remove} testId="notes-published-delete-dialog"
        title="Delete these published notes?" confirmText="Delete"
        description={del ? `${noteTitle(del)} will be removed from the Archive for everyone in the production. This can't be undone.` : ""} />
    </div>
  );
}
